import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Paper,
  IconButton,
  Tooltip,
  Autocomplete,
} from "@mui/material";
import { Search, Clear, Print } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import api from "../../api";

const ProductBillingHistory = () => {
  const navigate = useNavigate();
  const schoolId = localStorage.getItem("school_id");
  const [sessions, setSessions] = useState([]);
  const [filters, setFilters] = useState({
    session: null
  });
  const [classList, setClassList] = useState([]);
  const [sections, setSections] = useState([]);
  const [rolls, setRolls] = useState([]);
  const [products, setProducts] = useState([]);
  const [selectedClass, setSelectedClass] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedRoll, setSelectedRoll] = useState("");
  const [studentCode, setStudentCode] = useState("");
  const [selectedProduct, setSelectedProduct] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [historyData, setHistoryData] = useState([]);
  const [loading, setLoading] = useState(false);

  // --------------------------
  //  AUTH CHECK
  // --------------------------
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/login");
      return;
    }
  }, [navigate]);

  // 🧩 Init
  useEffect(() => {
    fetchSessions();
  }, []);

  // 🧩 Fetch Sessions
  const fetchSessions = async () => {
    try {
      const res = await api.get("school/academic_year/list", {
        headers: { "X-School-Id": schoolId },
      });

      if (res.data?.status !== false) {
        const data = res.data.data || [];
        data.sort((a, b) => (a.status === "active" ? -1 : 1));
        setSessions(data);

        // Auto-select active session
        if (!filters.session && data.length) {
          const active = data.find((s) => s.status === "active");
          setFilters((p) => ({ ...p, session: active || data[0] }));
        }
      }
    } catch {
      Swal.fire("Error", "Failed to fetch sessions", "error");
    }
  };

  // Fetch Classes
  useEffect(() => {
    api.get("/get-classes").then((res) => {
      setClassList(res.data?.data || []);
    });
  }, []);

  // Fetch Sections
  useEffect(() => {
    if (!selectedClass) return;
    api.get(`/get-sections/${selectedClass}`).then((res) => {
      setSections(res.data?.data || []);
    });
  }, [selectedClass]);

  // Fetch Rolls
  useEffect(() => {
    const fetchRoll = async () => {
      try {
        const res = await api.get(`/get-rolls/${selectedSection}`);

        if (res.data && res.data.success) {
          // ✅ remove duplicate roll_no
          const uniqueRolls = Array.from(
            new Map(
              res.data.data.map((r) => [r.roll_no, r])
            ).values()
          );

          setRolls(uniqueRolls);
          setSelectedRoll("");
        } else {
          setRolls([]);
        }
      } catch (error) {
        setRolls([]);
        console.error("Error fetching rolls:", error);
      }
    };

    if (selectedSection) fetchRoll();
  }, [selectedSection]);

  // Fetch Products
  useEffect(() => {
    api.get(`/product/bundle/list?school_id=${schoolId}`).then((res) => {
      setProducts(res.data?.data || []);
    });
  }, []);

  // Search Handler
  const handleSearch = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("school_id", schoolId);
      if (filters.session) params.append("academic_year_id", filters.session.id);
      if (selectedClass) params.append("class_id", selectedClass);
      if (selectedSection) params.append("section_id", selectedSection);
      if (selectedRoll) params.append("roll_no", selectedRoll);
      if (studentCode) params.append("student_code", studentCode);
      if (selectedProduct) params.append("product_variant_id", selectedProduct);
      if (fromDate) params.append("from_date", fromDate);
      if (toDate) params.append("to_date", toDate);

      const res = await api.get(`/student/product/billing/history?${params.toString()}`);
      if (res.data?.status) setHistoryData(res.data.data || []);
      else {
        setHistoryData([]);
        toast.info("No records found");
      }
    } catch {
      toast.error("Error fetching history!");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setSelectedClass("");
    setSelectedSection("");
    setSelectedRoll("");
    setStudentCode("");
    setSelectedProduct("");
    setFromDate("");
    setToDate("");
    setHistoryData([]);
  };

  // ✅ Invoice Print (Premium Full-Width Layout)
  const handleInvoicePrint = async (invoiceId, academicYearId) => {
    try {
      const res = await api.get(`/student/product/billing/invoice/${invoiceId}/${academicYearId}`);
      if (!res.data.status) {
        toast.error("Invoice not found!");
        return;
      }

      const data = res.data.data;
      const printWindow = window.open("", "_blank");

      const schoolLogo = data.school_logo
        ? `https://dgps.co.in/public/admin/assets/img/schools/logo/${data.school_logo}`
        : "https://dgps.co.in/public/admin/assets/img/logo.png";

      const getItemNetTotal = (item) =>
        parseFloat(item.net_subtotal)
          ? item.net_subtotal
          : item.total;
      printWindow.document.write(`
        <html>
        <head>
            <title>Product Invoice</title>
            <style>
            body { font-family: 'Poppins', sans-serif; margin: 0; padding: 20px; background: #f4f6f9; }
            .receipt-box {
                background: #fff; border: 2px dashed #ccc; border-radius: 12px; padding: 20px;
                margin: 20px auto; max-width: 900px; box-shadow: 0 4px 12px rgba(0,0,0,0.1);
            }
            .school-header { display: flex; align-items: center; justify-content: space-between; }
            .school-logo { width: 90px; height: 90px; object-fit: contain; }
            .school-text { flex-grow: 1; text-align: center; }
            .school-name { font-size: 22px; font-weight: bold; color: #002f6c; margin-bottom: 5px; }
            .school-contact { font-size: 13px; color: #555; }
            .invoice-title { background: #002f6c; color: #fff; text-align: center; padding: 10px; font-size: 18px; font-weight: 600; margin-top: 20px; border-radius: 8px; }
            .info-row { display: flex; justify-content: space-between; font-size: 14px; margin: 10px 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ccc; padding: 10px; font-size: 14px; text-align: left; }
            th { background: #e2eefd; color: #002f6c; }
            .amount-box {
                margin-top: 20px; text-align: right; font-weight: 600; font-size: 16px;
                background: #f0f6ff; padding: 10px; border-radius: 8px;
            }
            .footer { text-align: center; margin-top: 20px; font-size: 12px; color: #777; }
            @media print { body { background: white; padding: 0; } }
            </style>
        </head>
        <body>
            ${["Office Copy", "Student Copy"]
          .map(
            (copyType) => `
            <div class="receipt-box">
                <div class="school-header">
                <img src="${schoolLogo}" class="school-logo" alt="Logo"/>
                <div class="school-text">
                    <div class="school-name">${data.school_name}</div>
                    <div class="school-contact">
                    ${data.school_address}<br>
                    ${data.school_phone ? "Phone: " + data.school_phone + " | " : ""}${data.school_email || ""}
                    </div>
                </div>
                </div>

                <div class="invoice-title">Product Invoice</div>
                <div class="info-row"><strong>Invoice No:</strong> ${data.invoice_no}</div>

                <div class="info-row">
                <span><strong>Student:</strong> ${data.student_name} (${data.student_code})</span>
                <span><strong>Class:</strong> ${data.class_name} - ${data.section_name}</span>
                <span><strong>Roll:</strong> ${data.roll_no}</span>
                </div>

                <table>
                <thead>
                    <tr>
                    <th>Product</th>
                    <th>Variant</th>
                    <th>Qty</th>
                    <th>Price</th>
                    <th>Discount</th>
                    <th>Total</th>
                    </tr>
                </thead>
                <tbody>
                    ${data.items
                .map(
                  (item) => `
                    <tr>
                        <td>${item.product_name}</td>
                        <td>${item.variant_name}</td>
                        <td>${item.quantity}</td>
                        <td>₹ ${item.price}</td>
                        <td>₹ ${item.item_discount}</td>
                        <td>₹ ${getItemNetTotal(item)}</td>
                    </tr>`
                )
                .join("")}
                </tbody>
                </table>

                <div class="amount-box">
                <div>Total Amount: ₹${data.total_amount}</div>
                <div>Discount: ₹${data.discount}</div>
                <div>Net Payable: <strong>₹${data.net_amount}</strong></div>
                </div>

                <div class="info-row">
                <span><strong>Payment Mode:</strong> ${data.payment_mode.toUpperCase()}</span>
                <span><strong>Status:</strong> ${data.payment_status.toUpperCase()}</span>
                <span><strong>Created By:</strong> ${data.created_by}</span>
                </div>

                <div class="footer">
                This is a system-generated invoice — no signature required.
                <br><small>${copyType}</small>
                </div>
            </div>`
          )
          .join("")}
            <script>window.onload = () => window.print(); window.onafterprint = () => window.close();</script>
        </body>
        </html>`);
      printWindow.document.close();
    } catch (err) {
      toast.error("Error while printing invoice!");
    }
  };

  // ✅ Thermal Printer Invoice Layout (Compact Version)
  const handleThermalInvoicePrint = async (invoiceId, academicYearId) => {
    try {
      const res = await api.get(`/student/product/billing/invoice/${invoiceId}/${academicYearId}`);
      if (!res.data.status) {
        toast.error("Invoice not found!");
        return;
      }

      const data = res.data.data;
      const printWindow = window.open("", "_blank");

      const schoolLogo = data.school_logo_bk
        ? `https://dgps.co.in/public/admin/assets/img/schools/logo/${data.school_logo_bk}`
        : "https://dgps.co.in/public/admin/assets/img/logo.png";


      const getItemNetTotal = (item) =>
        parseFloat(item.net_subtotal)
          ? item.net_subtotal
          : item.total;


      // 🧾 Thermal Print Layout
      printWindow.document.write(`
      <html>
      <head>
        <title>Thermal Product Invoice</title>
        <style>
          @page {
            size: 80mm auto;
            margin: 2mm;
          }
          body {
            font-family: 'Segoe UI', sans-serif;
            width: 70mm;
            margin: 0 auto;
            padding: 2mm;
            background: #fff;
            color: #000;
            font-size: 11px;
            -webkit-print-color-adjust: exact !important;
            image-rendering: pixelated;
          }

          .center { text-align: center; }
          .right { text-align: right; }
          .bold { font-weight: bold; }
          .line { border-bottom: 1px dashed #000; margin: 3px 0; }
          .school-logo {
            width: 60px;
            height: 60px;
            display: block;
            margin: 0 auto 3px;
            object-fit: contain;
            filter: grayscale(100%) brightness(1.2) contrast(100%);
          }
          .school-name { font-size: 13px; font-weight: bold; text-transform: uppercase; }
          .contact { font-size: 10px; line-height: 1.3; margin-bottom: 5px; }

          .title-bar {
            background: #000; color: #fff; text-align: center;
            font-weight: bold; font-size: 11px; padding: 3px 0;
            border-radius: 2px; margin: 4px 0;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            font-size: 11px;
            margin: 2px 0;
            word-wrap: break-word;
            width: 100%;
          }

          table {
            width: 100%;
            border-collapse: collapse;
            margin-top: 2px;
            font-size: 11px;
          }
          th, td {
            padding: 2px 0;
            text-align: left;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          th {
            border-bottom: 1px solid #000;
            font-weight: bold;
          }
          td {
            border-bottom: 1px dotted #000;
          }

          .amount-box {
            margin-top: 4px;
            border-top: 1px dashed #000;
            padding-top: 3px;
            font-size: 11px;
          }

          .footer {
            text-align: center;
            font-size: 9.5px;
            margin-top: 6px;
            border-top: 1px dashed #000;
            padding-top: 3px;
            font-style: italic;
            line-height: 1.3;
          }

          @media print {
            body { -webkit-print-color-adjust: exact !important; }
              .school-logo {
                filter: grayscale(100%) brightness(1.2) contrast(100%);
              }
          }
        </style>
      </head>
      <body>
        <div class="center">
          <img src="${schoolLogo}" class="school-logo" alt="Logo"/>
          <div class="school-name">${data.school_name}</div>
          <div class="contact">
            ${data.school_address ? `${data.school_address}<br/>` : ""}
            ${data.school_phone ? `Ph: ${data.school_phone}<br/>` : ""}
            ${data.school_email || ""}
          </div>
        </div>

        <div class="title-bar">STUDENT COPY - PRODUCT INVOICE</div>

        <div class="info-row"><span>Invoice No:</span><span>${data.invoice_no}</span></div>
        <div class="info-row"><span>Student:</span><span>${data.student_name}</span></div>
        <div class="info-row"><span>Class:</span><span>${data.class_name}-${data.section_name}</span></div>
        <div class="info-row"><span>Roll:</span><span>${data.roll_no}</span></div>
        <div class="line"></div>

        <table>
          <thead>
            <tr>
              <th>Product</th>
              <th>Qty</th>
              <th class="right">Amt</th>
              <th class="right">Disc</th>
              <th class="right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${data.items.map(
        (item) => `
                <tr>
                  <td>${item.product_name}</td>
                  <td>${item.quantity}</td>
                  <td class="right">₹${item.price}</td>
                  <td class="right">₹${item.item_discount}</td>
                  <td class="right">₹${getItemNetTotal(item)}</td>
                </tr>
              `
      ).join("")}
          </tbody>
        </table>

        <div class="amount-box">
          <div class="info-row"><span>Total:</span><span>₹${data.total_amount}</span></div>
          <div class="info-row"><span>Discount:</span><span>₹${data.discount}</span></div>
          <div class="info-row bold"><span>Net Payable:</span><span>₹${data.net_amount}</span></div>
        </div>

        <div class="line"></div>
        <div class="info-row"><span>Payment:</span><span>${data.payment_mode.toUpperCase()}</span></div>
        <div class="info-row"><span>Status:</span><span>${data.payment_status.toUpperCase()}</span></div>
        <div class="info-row"><span>Created By:</span><span>${data.created_by}</span></div>

        <div class="footer">
          Thank you for your payment!<br/>
          This is a system-generated invoice.<br/>
          No signature required.
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
          window.onafterprint = function() {
            window.close();
          };
        </script>
      </body>
      </html>
    `);

      printWindow.document.close();
    } catch (err) {
      toast.error("Error while printing thermal invoice!");
    }
  };

  // DataGrid Columns
  const columns = [
    {
      field: "actions",
      headerName: "Action",
      width: 160,
      renderCell: (params) => (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "center",
            gap: "2px",
            width: "100%",
            margin: "5px",
          }}
        >
          {/* 🖨️ Normal Print */}
          <Tooltip title="Normal Print Invoice">
            <div
              onClick={() => handleInvoicePrint(params.row.id, params.row.academic_year_id)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                cursor: "pointer",
                padding: "6px",
                borderRadius: "8px",
                background: "linear-gradient(90deg, #42a5f5, #1e88e5)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "12px",
                boxShadow: "0 2px 6px rgba(33, 150, 243, 0.3)",
                transition: "all 0.25s ease",
                width: "50px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow = "0 4px 10px rgba(33,150,243,0.4)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(33,150,243,0.3)";
              }}
            >
              <Print style={{ fontSize: 16 }} />
              <span>Print</span>
            </div>
          </Tooltip>

          {/* 🔥 Thermal Print */}
          <Tooltip title="Thermal Print Invoice">
            <div
              onClick={() => handleThermalInvoicePrint(params.row.id, params.row.academic_year_id)}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "6px",
                cursor: "pointer",
                padding: "6px",
                borderRadius: "8px",
                background: "linear-gradient(90deg, #43a047, #2e7d32)",
                color: "#fff",
                fontWeight: 600,
                fontSize: "12px",
                boxShadow: "0 2px 6px rgba(67,160,71,0.35)",
                transition: "all 0.25s ease",
                width: "90px",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.transform = "scale(1.05)";
                e.currentTarget.style.boxShadow = "0 4px 10px rgba(67,160,71,0.45)";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.transform = "scale(1)";
                e.currentTarget.style.boxShadow = "0 2px 6px rgba(67,160,71,0.35)";
              }}
            >
              <Print style={{ fontSize: 16 }} />
              <span>Thermal</span>
            </div>
          </Tooltip>
        </div>
      ),
    },

    { field: "invoice_no", headerName: "Invoice No", width: 180 },
    { field: "student_name", headerName: "Student Name", width: 160 },
    { field: "student_code", headerName: "Code", width: 100 },
    { field: "class_name", headerName: "Class", width: 100 },
    { field: "section_name", headerName: "Section", width: 100 },
    { field: "roll_no", headerName: "Roll", width: 80 },
    {
      field: "total_amount",
      headerName: "Total (₹)",
      width: 110,
      valueFormatter: (params) => parseFloat(params.value).toFixed(2),
    },
    {
      field: "net_amount",
      headerName: "Net (₹)",
      width: 110,
      valueFormatter: (params) => parseFloat(params.value).toFixed(2),
    },
    {
      field: "payment_mode",
      headerName: "Mode",
      width: 100,
      valueFormatter: (params) => params.value.toUpperCase(),
    },
    {
      field: "payment_status",
      headerName: "Status",
      width: 100,
      valueFormatter: (params) => params.value.toUpperCase(),
    },
    {
      field: "created_at",
      headerName: "Date",
      width: 160,
      valueFormatter: (params) =>
        new Date(params.value).toLocaleString("en-IN", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }),
    },
  ];

  return (
    <Box sx={{ background: "#f4f6f8", minHeight: "100vh", p: 0 }}>
      <ToastContainer position="top-right" autoClose={2000} theme="colored" />
      <Card sx={{ mx: "auto", borderRadius: 4, boxShadow: 6 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={700} color="primary" gutterBottom>
            📚 Student Product Billing History
          </Typography>

          {/* 🔍 Filter Section */}
          <Paper sx={{ p: 2.5, borderRadius: 2, mb: 3, background: "#fafafa" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={2.2}>
                <Autocomplete
                  options={sessions}
                  getOptionLabel={(opt) =>
                    `${opt.session_name || ""}${opt.status ? ` (${opt.status})` : ""}`
                  }
                  value={filters.session}
                  onChange={(_, v) => setFilters((p) => ({ ...p, session: v }))}
                  renderInput={(params) => (
                    <TextField {...params} label="Select Session" fullWidth />
                  )}
                  clearOnEscape
                />
              </Grid>
              <Grid item xs={12} md={2.2}>
                <Autocomplete
                  options={classList}
                  getOptionLabel={(o) => o.class_name}
                  value={classList.find((x) => x.id === selectedClass) || null}
                  onChange={(e, v) => setSelectedClass(v ? v.id : "")}
                  renderInput={(p) => <TextField {...p} label="Class" />}
                />
              </Grid>
              <Grid item xs={12} md={2.2}>
                <Autocomplete
                  options={sections}
                  getOptionLabel={(o) => o.section_name}
                  value={sections.find((x) => x.id === selectedSection) || null}
                  onChange={(e, v) => setSelectedSection(v ? v.id : "")}
                  renderInput={(p) => <TextField {...p} label="Section" />}
                />
              </Grid>
              <Grid item xs={12} md={2.2}>
                <Autocomplete
                  options={rolls}
                  getOptionLabel={(o) => o.roll_no?.toString() || ""}
                  value={rolls.find((r) => r.roll_no === selectedRoll) || null}
                  onChange={(e, v) => setSelectedRoll(v ? v.roll_no : "")}
                  renderInput={(p) => <TextField {...p} label="Roll" />}
                />
              </Grid>
              <Grid item xs={12} md={2.2}>
                <TextField
                  label="Student Code"
                  value={studentCode}
                  onChange={(e) => setStudentCode(e.target.value)}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={2.2}>
                <Autocomplete
                  options={products.flatMap((p) => p.variants || [])}
                  getOptionLabel={(o) =>
                    `${o.variant_name || ""} (${o.product_name || ""})`
                  }
                  value={
                    products
                      .flatMap((p) => p.variants || [])
                      .find((v) => v.variant_id === selectedProduct) || null
                  }
                  onChange={(e, v) => setSelectedProduct(v ? v.variant_id : "")}
                  renderInput={(p) => <TextField {...p} label="Product" />}
                />
              </Grid>
              <Grid item xs={6} md={1.5}>
                <TextField
                  type="date"
                  label="From"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={6} md={1.5}>
                <TextField
                  type="date"
                  label="To"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} textAlign="center">
                <Button
                  variant="contained"
                  startIcon={<Search />}
                  sx={{
                    mr: 2,
                    background: "linear-gradient(90deg,#4e54c8,#8f94fb)",
                    fontWeight: 600,
                  }}
                  onClick={handleSearch}
                >
                  Search
                </Button>
                <Button variant="outlined" startIcon={<Clear />} onClick={handleClear}>
                  Clear
                </Button>
              </Grid>
            </Grid>
          </Paper>

          {/* 📋 Table Section */}
          <Box sx={{ height: 600, width: "100%" }}>
            <DataGrid
              rows={historyData}
              columns={columns}
              getRowId={(r) => r.id}
              pageSize={8}
              loading={loading}
              sx={{
                background: "#fff",
                borderRadius: 2,
                "& .MuiDataGrid-columnHeaders": {
                  background: "#e2eefd",
                  color: "#002f6c",
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default ProductBillingHistory;
